import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Clock,
  Play,
  Pause,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Timer,
  Calendar,
  Zap
} from 'lucide-react';

import { API_BASE_URL as API } from '../utils/apiConfig';

export default function SyncSchedulerStatus() {
  const { isSuperAdmin } = useAuth();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Fetch scheduler status
  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        fetch(`${API}/api/scheduler/status`, { credentials: 'include' }),
        fetch(`${API}/api/scheduler/history?limit=5`, { credentials: 'include' })
      ]);
      
      if (statusRes.ok) {
        const data = await statusRes.json();
        setStatus(data);
      }
      
      if (historyRes.ok) {
        const data = await historyRes.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchStatus();
      // Refresh every 30 seconds
      const interval = setInterval(fetchStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [fetchStatus, isSuperAdmin]);

  // Start scheduler
  const handleStart = async () => {
    setActionLoading('start');
    try {
      const response = await fetch(`${API}/api/scheduler/start`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      toast.success(data.message);
      fetchStatus();
    } catch (error) {
      toast.error('Failed to start scheduler');
    } finally {
      setActionLoading(null);
    }
  };

  // Stop scheduler
  const handleStop = async () => {
    setActionLoading('stop');
    try {
      const response = await fetch(`${API}/api/scheduler/stop`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      toast.success(data.message);
      fetchStatus();
    } catch (error) {
      toast.error('Failed to stop scheduler');
    } finally {
      setActionLoading(null);
    }
  };

  // Trigger immediate sync
  const handleTrigger = async () => {
    setActionLoading('trigger');
    try {
      const response = await fetch(`${API}/api/scheduler/trigger`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      toast.success(data.message);
      fetchStatus();
    } catch (error) {
      toast.error('Failed to trigger sync');
    } finally {
      setActionLoading(null);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Never';
    try {
      return new Date(timestamp).toLocaleString('en-IN', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'Asia/Kolkata'
      });
    } catch {
      return timestamp;
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  if (!isSuperAdmin) {
    return null; // Don't render for non-super admins
  }

  const isRunning = status?.status === 'running';
  const isSyncing = status?.current_status === 'syncing';

  return (
    <Card className="bg-gray-800/50 border-gray-700" data-testid="sync-scheduler-status">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Timer className="w-5 h-5 text-blue-400" />
              Auto-Sync Scheduler
            </CardTitle>
            <CardDescription>
              Hourly automatic data sync for all brands
            </CardDescription>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge className={isRunning ? 'bg-green-600' : 'bg-gray-600'}>
              {isRunning ? 'Running' : 'Stopped'}
            </Badge>
            {isSyncing && (
              <Badge className="bg-blue-600 animate-pulse">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Syncing
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-900/50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Interval</p>
                <p className="text-lg font-bold text-white">
                  {status?.interval_hours || 1} Hour
                </p>
              </div>
              <div className="bg-gray-900/50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Last Cycle</p>
                <p className="text-sm font-medium text-white">
                  {formatTime(status?.last_cycle_end)}
                </p>
              </div>
              <div className="bg-gray-900/50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Brands Synced</p>
                <p className="text-lg font-bold text-white">
                  {status?.last_cycle_brands || 0}
                </p>
              </div>
              <div className="bg-gray-900/50 p-3 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Success Rate</p>
                <p className="text-lg font-bold text-green-400">
                  {status?.last_cycle_brands > 0 
                    ? Math.round((status.last_cycle_successful / status.last_cycle_brands) * 100)
                    : 0}%
                </p>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2 pt-2">
              {isRunning ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStop}
                  disabled={actionLoading === 'stop'}
                  className="border-red-600 text-red-400 hover:bg-red-900/20"
                >
                  {actionLoading === 'stop' ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Pause className="w-4 h-4 mr-1" />
                  )}
                  Stop
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStart}
                  disabled={actionLoading === 'start'}
                  className="border-green-600 text-green-400 hover:bg-green-900/20"
                >
                  {actionLoading === 'start' ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 mr-1" />
                  )}
                  Start
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleTrigger}
                disabled={actionLoading === 'trigger' || isSyncing}
                className="border-blue-600 text-blue-400 hover:bg-blue-900/20"
              >
                {actionLoading === 'trigger' ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-1" />
                )}
                Sync Now
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchStatus}
                className="ml-auto"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {/* Recent History */}
            {history.length > 0 && (
              <div className="pt-2">
                <p className="text-xs text-gray-500 uppercase mb-2">Recent Sync Cycles</p>
                <div className="space-y-1">
                  {history.slice(0, 3).map((cycle, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between text-sm py-1.5 px-2 bg-gray-900/30 rounded"
                    >
                      <div className="flex items-center gap-2">
                        {cycle.failed > 0 ? (
                          <XCircle className="w-4 h-4 text-red-400" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                        )}
                        <span className="text-gray-300">
                          {formatTime(cycle.cycle_start)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-green-400">{cycle.successful} OK</span>
                        {cycle.failed > 0 && (
                          <span className="text-red-400">{cycle.failed} Failed</span>
                        )}
                        <span className="text-gray-500">
                          {formatDuration(cycle.duration_seconds)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
